import type { components } from '../api/generated/schema'
import type { AuthUser, MfaChallenge } from './port'

type Profile = components['schemas']['UserResponse']

/**
 * Six states, one union.
 *
 * Shaped after `AsyncState` in platform/async/Async.tsx for the same reason:
 * `profile` is reachable only where it exists, so no screen can read it during
 * bootstrap. The two arms worth naming:
 *
 *   `unknown`             Firebase has not reported yet. NOT `signedOut` — the
 *                         guard would bounce every returning user to /signin
 *                         for a frame.
 *   `sessionUnavailable`  Firebase says yes, our backend did not answer. A real
 *                         state rather than a null profile, because the router
 *                         has to render a different screen for it and a
 *                         nullable field is a check somebody forgets.
 */
export type AuthState =
  | { status: 'unknown' }
  | { status: 'signedOut' }
  | { status: 'mfaRequired'; challenge: MfaChallenge }
  | { status: 'bootstrapping'; user: AuthUser }
  | { status: 'sessionUnavailable'; user: AuthUser }
  | { status: 'signedIn'; user: AuthUser; profile: Profile }

/**
 * What can happen to a session, in the app's vocabulary rather than the SDK's.
 *
 * Named for the event rather than the resulting state — `established`, not
 * `setSignedIn` — because which state an event produces is this file's decision
 * and not the caller's. That is the whole point of the exercise below.
 */
export type AuthEvent =
  | { type: 'reported'; user: AuthUser }
  | { type: 'refreshed'; user: AuthUser }
  /** The USER asked to leave. Legal from anywhere; never something to argue with. */
  | { type: 'cleared' }
  /**
   * The LISTENER saw no user. A different fact from `cleared`, and collapsing
   * the two into one event cost the phone path: Firebase has no signed-in user
   * during an MFA challenge, so this fires *because* a challenge is outstanding,
   * and treating it as a sign-out wiped the challenge the moment it arrived
   * second. Nothing orders it against `getRedirectResult`.
   */
  | { type: 'reportedNone' }
  | { type: 'established'; user: AuthUser; profile: Profile }
  | { type: 'unavailable'; user: AuthUser }
  | { type: 'challenged'; challenge: MfaChallenge }
  /**
   * The profile changed without the SESSION changing.
   *
   * `established` carries a profile too, but it means "a session now exists" —
   * dispatching it to edit one field would re-enter `signedIn` from wherever
   * the state happened to be, including from a raised MFA challenge. This event
   * only ever refines a profile that is already there.
   */
  | { type: 'profileUpdated'; profile: Profile }

export const INITIAL_AUTH_STATE: AuthState = { status: 'unknown' }

/**
 * Does this state already hold that user?
 *
 * Exported because the reducer and `AuthProvider`'s port listener both need the
 * answer and must not each write it down. The listener originally restated the
 * condition, and then — to remove the duplication — inferred it from the table
 * by checking whether `refreshed` came back as the same object. Both were
 * wrong in the same way: the first drifts silently, and the second reads the
 * identity contract as "refused" when the reducer's other arms use it for
 * "nothing changed". Those coincide only while `refreshed` always allocates,
 * which nothing required — web-architect added the obvious idempotence
 * optimisation and every test stayed green while the listener began firing a
 * session POST on each no-op token refresh.
 *
 * One predicate, two callers, no inference.
 */
export function holdsUser(
  state: AuthState,
  uid: string,
): state is Extract<AuthState, { status: 'signedIn' | 'bootstrapping' }> {
  return (
    (state.status === 'signedIn' || state.status === 'bootstrapping') && state.user.uid === uid
  )
}

/**
 * The transition table. Pure, exhaustive, and the reason this file exists.
 *
 * CLAUDE.md puts UI state behind a reducer, and the reason showed up as a
 * defect rather than a style note. The context spreads all seven actions onto
 * every arm, so every action is callable from every state — and `signIn()`
 * while already `signedIn` moved the app to `mfaRequired`, silently discarding
 * `profile`. web-architect found that one by reading; the same shape produced
 * two of the phase's real bugs, where a stale `startSession()` resolved into a
 * signed-out app and rebuilt `signedIn` on top of a null token provider.
 *
 * **Rejecting a transition means returning the SAME OBJECT**, not a copy. The
 * caller uses identity to decide whether to re-render, so a rejected event
 * costs nothing.
 *
 * This does not replace the generation ticket in `AuthProvider`, and the two
 * are not redundant. The table cannot tell WHICH in-flight bootstrap is
 * resolving — both target `bootstrapping` — and it cannot stop a side effect
 * that runs before the dispatch, which is exactly what `setAnalyticsUser` is.
 * The ticket guards the side effects; the table guards the state.
 */
export function reduce(state: AuthState, event: AuthEvent): AuthState {
  switch (event.type) {
    // Always legal, from anywhere. A sign-out is never something to argue with,
    // and the idempotent case returns the same object so it does not re-render.
    case 'cleared':
      return state.status === 'signedOut' ? state : { status: 'signedOut' }

    // Firebase reporting no user, which is NOT the same statement. Refused from
    // `mfaRequired`, where having no signed-in user is the definition of the
    // state rather than news about it — see the event's own comment.
    case 'reportedNone':
      if (state.status === 'mfaRequired') return state
      return state.status === 'signedOut' ? state : { status: 'signedOut' }

    // Also legal from anywhere: Firebase reporting a user supersedes whatever we
    // thought, including a challenge the user has just cleared.
    case 'reported':
      return { status: 'bootstrapping', user: event.user }

    // A fresher snapshot of a user we already hold — a token refresh carrying
    // `emailVerified: true`, which is how M1-R4's banner learns to clear.
    // Meaningless anywhere the uid is not already in hand, and rejected there
    // rather than inventing a user for an arm that has none.
    case 'refreshed':
      return holdsUser(state, event.user.uid) ? { ...state, user: event.user } : state

    // Only out of `bootstrapping`. A session POST that resolves into a
    // signed-out app is stale by definition, and applying it was the defect
    // that cost the most this phase: signedIn UI over a null token provider,
    // every request 401ing.
    case 'established':
      return state.status === 'bootstrapping'
        ? { status: 'signedIn', user: event.user, profile: event.profile }
        : state

    case 'unavailable':
      return state.status === 'bootstrapping'
        ? { status: 'sessionUnavailable', user: event.user }
        : state

    // The one the reducer was asked for. A challenge cannot dislodge an
    // established session, nor interrupt a bootstrap or a retry — in all three
    // Firebase has already reported a user, so a challenge arriving after them
    // lost its race.
    case 'profileUpdated':
      // Only where a profile exists. From any other arm this is a no-op rather
      // than a transition nobody designed — the rule `retrySession` states.
      return state.status === 'signedIn' ? { ...state, profile: event.profile } : state

    case 'challenged':
      return state.status === 'signedIn' ||
        state.status === 'bootstrapping' ||
        state.status === 'sessionUnavailable'
        ? state
        : { status: 'mfaRequired', challenge: event.challenge }
  }
}
